import React, {useEffect, useState} from 'react';
import FullCalendar, {EventContentArg} from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import '@fullcalendar/common/main.css';
import moment from 'moment';
import {endOfWeek, startOfWeek} from 'date-fns';
import {useDatabase} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import {Colors} from 'src/styles';
import {appointmentLocations} from 'src/form-inputs/appointment-location-input';
import {useDimensions} from '@react-native-community/hooks';
import StyleWrapper from 'src/modules/scheduling/components/web-calendar-wrapper';
import CalendarCard from 'src/modules/scheduling/components/calendar-card';
import StatusLegend from 'src/modules/scheduling/components/status-legend';
import LocationLegend from 'src/modules/scheduling/components/location-legend';
import CancellationLegend from 'src/modules/scheduling/components/cancellation-legend';
import {useSelector, useDispatch} from 'react-redux';
import {setFilters} from 'src/redux/scheduling-filters';
import {RRule, RRuleSet, rrulestr} from 'rrule';
import CancellationListItem from 'src/modules/scheduling/components/cancellation-list-item';
import {cancelReason} from 'src/modules/scheduling/components/cancellation-list-item';
import AppointmentListItem from 'src/modules/scheduling/components/appointment-list-item';
import InProgressCalendarItem from 'src/modules/scheduling/components/in-progress-calendar-item';
import AppointmentCalendarItem from 'src/modules/scheduling/components/appointment-calendar-item';
import NotDraggableModal from 'src/modules/scheduling/components/not-draggable-modal';
import EditRecurringAppointment from 'src/modules/scheduling/components/edit-recurring-appointment';
import {Appointment, Participant, Session, User} from 'src/models';
import {View} from 'react-native';
import CsvExport from 'src/common-components/csv-export/components';
import {useStyle} from 'src/providers/style';
import Spinner from 'react-native-loading-spinner-overlay';
import {useSessionDateSync} from 'src/hooks/useSessionDateSync';

const Calendar = ({
  assignedOnly,
  profile,
  filters,
  update,
  canAdd,
  addCallback,
  canEdit,
  editCallback,
  canDelete,
  deleteCallback,
}: any) => {
  const dimensions = useDimensions();
  const database = useDatabase();
  const styles = useStyle();
  const dispatch = useDispatch();

  const [items, setItems] = useState<any>([]);
  const [dates, setDates] = useState<{start?: Date; end?: Date}>({});
  const [selectedEvent, setSelectedEvent] = useState(false);
  const [cannotMoveEvent, setCannotMoveEvent] = useState(false);
  const {isLoading, syncDateRange} = useSessionDateSync();

  const [editRecurringAppointment, setEditRecurringAppointment] = useState<
    boolean | any
  >(false);

  const {selectedGroup, userId} = useSelector(
    (state: any) => state.authentication,
  );

  const getAppointmentColor = async (
    appointment: any,
    date: any,
    supervision: boolean,
  ) => {
    let appointmentColor: string | undefined = '';
    let sessionStatus: string | undefined = '';
    const sessions =
      filters.scheduleBy === 'cancel'
        ? []
        : await database
            .get('sessions')
            .query(
              Q.where('type', supervision ? 'supervision' : 'session'),
              Q.where('appointment_id', appointment.id),
              Q.where('session_date', moment(date).format('YYYY-MM-DD')),
              Q.where('deleted_at', null),
              Q.sortBy('end_timestamp', Q.desc),
            )
            .fetch();

    const session = sessions[0];
    if (appointment?.type === 'event') {
      sessionStatus = 'Static Event';
    } else if (sessions.length === 0 || !session?.startTimestamp) {
      sessionStatus = 'Not Started';
    } else if (!session?.endTimestamp) {
      sessionStatus = 'In Progress';
    } else if (!session.submissionTimestamp) {
      sessionStatus = 'Missing Values';
    } else {
      sessionStatus = 'Completed';
    }

    if (filters.scheduleBy === 'location') {
      appointmentColor = appointmentLocations.find(
        (loc: any) => loc.value === appointment?.location,
      )?.color;
    } else if (filters.scheduleBy === 'status') {
      const STATUS_COLORS: Record<string, string> = {
        'Static Event': Colors.BLAZE_ORANGE,
        'Not Started': Colors.TEAL_500,
        'In Progress': Colors.LIME_500,
        'Missing Values': Colors.RUBY_500,
        Completed: Colors.ARCTIC,
      };
      appointmentColor = STATUS_COLORS[sessionStatus] || Colors.TEAL_500;
    } else if (filters.scheduleBy === 'cancel') {
      const CANCEL_COLORS: Record<string, string> = {
        staff: Colors.TEAL_500,
        client: Colors.LIME_500,
        noShow: Colors.RUBY_500,
        absent: Colors.VIOLET_500,
        error: Colors.TORCH_RED_LIGHT,
      };
      appointmentColor =
        CANCEL_COLORS[appointment?.cancellationReason] || Colors.ORANGE;
    }
    return {appointmentColor, sessionStatus};
  };

  const recalculateAppointments = async (
    appointmentStartDate: Date,
    appointmentEndDate: Date,
  ) => {
    const locationQuery = [];
    if (filters?.locations && filters.locations.length) {
      locationQuery.push(Q.where('location', Q.oneOf(filters.locations)));
    }

    const userStore = {};
    const clients = [];
    const users = [];
    for (const participant of filters.participants) {
      if (participant.type === 'patient') {
        clients.push(participant);
      } else {
        userStore[participant.value] = participant;
        users.push(participant);
      }
    }

    let clientQueries: [Q.On[]] | any[] = [];
    if (clients.length) {
      clientQueries = [
        Q.on(
          'participants',
          Q.and(
            Q.where('patient_id', Q.oneOf(clients.map(client => client.value))),
            Q.where('deleted_at', null),
          ),
        ),
      ];
    } else {
      clientQueries = [];
    }

    let usersQueries: [Q.On[]] | any[] = [];
    const assignedOnlyQuery = assignedOnly
      ? [
          Q.on(
            'participants',
            Q.and(
              Q.where('user_id', Q.eq(profile.id)),
              Q.where('deleted_at', null),
            ),
          ),
        ]
      : [];
    if (assignedOnly) {
      usersQueries = [
        Q.on(
          'participants',
          Q.and(
            Q.where('user_id', Q.eq(profile.id)),
            Q.where('deleted_at', null),
          ),
        ),
      ];
    }
    let billingCodeQueries = [];
    if (filters?.types && filters.types.length) {
      const types = [];
      for (const code of filters.types) {
        types.push(Q.where('cpt', Q.includes(code)));
      }
      billingCodeQueries.push(Q.or(types));
    }
    if (users.length) {
      usersQueries = [
        ...usersQueries,
        Q.on(
          'participants',
          Q.and(
            Q.where('user_id', Q.oneOf(users.map(user => user.value))),
            Q.where('deleted_at', null),
            ...billingCodeQueries,
          ),
        ),
      ];
    } else {
      if (billingCodeQueries.length > 0) {
        usersQueries = [
          ...assignedOnlyQuery,
          Q.on(
            'participants',
            Q.and(Q.where('deleted_at', null), ...billingCodeQueries),
          ),
        ];
      } else {
        usersQueries = assignedOnlyQuery;
      }
    }

    const filterCancelQuery =
      filters?.scheduleBy === 'cancel'
        ? Q.where('deleted_at', Q.notEq(null))
        : Q.where('deleted_at', null);
    const queriedAppointments = await database
      .get('appointments')
      .query(
        Q.where('_partition', selectedGroup),
        Q.or(
          Q.and(
            Q.where('rrule', ''),
            Q.where('date', Q.gt(appointmentStartDate.getTime())),
            Q.where('date', Q.lt(appointmentEndDate.getTime())),
          ),
          Q.and(
            Q.where('rrule', Q.notEq('')),
            Q.or(
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.gt(appointmentStartDate.getTime()),
                ),
                Q.where('start_timestamp', Q.lt(appointmentEndDate.getTime())),
              ),
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.lt(appointmentStartDate.getTime()),
                ),
                Q.or(
                  Q.where('end_timestamp', Q.eq(0)),
                  Q.where(
                    'end_timestamp',
                    Q.gt(appointmentStartDate.getTime()),
                  ),
                ),
              ),
            ),
          ),
        ),
        ...usersQueries,
        ...clientQueries,
        ...locationQuery,
        filterCancelQuery,
      );

    const curatedAppointments = [];
    for (const appointment of queriedAppointments) {
      const appointmentPatients = await appointment.patients.fetch();
      const patient = appointmentPatients?.[0];
      let clientName = `${patient?.firstName} ${patient?.lastName}`;

      const staffParticipants = await appointment.staffParticipants.fetch();

      if (appointment.rrule) {
        const rruleSet = rrulestr(appointment.rrule, {
          forceset: true,
        });

        // Always use UTC versions of the start and end window
        const startTime = new Date(
          Date.UTC(
            appointmentStartDate.getUTCFullYear(),
            appointmentStartDate.getUTCMonth(),
            appointmentStartDate.getUTCDate(),
            0,
            0,
            0,
          ),
        );
        const endTime = new Date(
          Date.UTC(
            appointmentEndDate.getUTCFullYear(),
            appointmentEndDate.getUTCMonth(),
            appointmentEndDate.getUTCDate(),
            23,
            59,
            59,
          ),
        );

        // Get all rrule occurrences in UTC within the date range
        const allOccurrences = rruleSet
          .between(startTime, endTime, true)
          .map(rruleDate => {
            const realDate = new Date(
              rruleDate.getFullYear(),
              rruleDate.getMonth(),
              rruleDate.getDate(),
            );
            realDate.setTime(
              rruleDate.getTime() +
                rruleSet.options.dtstart.getTimezoneOffset() * 60 * 1000,
            );
            return realDate;
          });
        // Convert EXDATEs to ISO strings for exact UTC matching
        const excludedDates = new Set(
          rruleSet.exdates().map(d => d.toISOString().split('T')[0]),
        );

        // Filter out excluded occurrences
        for (const date of allOccurrences) {
          if (excludedDates.has(date.toISOString().split('T')[0])) {
            continue;
          }
          for (const staffParticipant of staffParticipants) {
            if (
              !Object.keys(userStore).length ||
              userStore?.[staffParticipant.userId]
            ) {
              let queries = [];
              if (staffParticipant?.supervision) {
                queries.push(Q.where('type', 'supervision'));
              } else {
                queries.push(Q.where('type', 'session'));
              }
              let apptSessions = await database
                .get(Session.table)
                .query(
                  Q.where('appointment_id', appointment.id),
                  Q.where('session_date', moment(date).format('YYYY-MM-DD')),
                  Q.where('deleted_at', null),
                  Q.where('end_timestamp', Q.notEq(null)),
                  ...queries,
                )
                .fetch();

              let useSessionTime = apptSessions?.length > 0;
              //check if end times are different thn what is scheduled
              let appointmentChanged = false;
              let sessionStartDate;
              let sessionEndDate;
              let apptStartDate = moment(staffParticipant.startTime);
              let apptEndDate = moment(staffParticipant.endTime);
              const startDate = useSessionTime
                ? moment(apptSessions[0]?.editedStartTimestamp)
                : apptStartDate;
              const endDate = useSessionTime
                ? moment(apptSessions[0]?.editedEndTimestamp)
                : apptEndDate;
              if (useSessionTime) {
                sessionStartDate = moment(
                  apptSessions[0]?.editedStartTimestamp,
                );
                sessionEndDate = moment(apptSessions[0]?.editedEndTimestamp);
                if (
                  sessionStartDate.format('HH:mm') !==
                    apptStartDate.format('HH:mm') ||
                  sessionEndDate.format('HH:mm') !== apptEndDate.format('HH:mm')
                ) {
                  appointmentChanged = true;
                }
              }
              const start = moment(date)
                .set({
                  hour: startDate.get('hour'),
                  minute: startDate.get('minute'),
                  second: startDate.get('second'),
                })
                .toDate();
              const end = moment(date)
                .set({
                  hour: endDate.get('hour'),
                  minute: endDate.get('minute'),
                  second: endDate.get('second'),
                })
                .toDate();

              const {appointmentColor, sessionStatus} =
                await getAppointmentColor(
                  appointment,
                  date,
                  staffParticipant.supervision,
                );
              let category = 'General Event';
              if (appointment.type === 'event' && appointment?.eventId) {
                const categoryEvent = await database
                  .get('event_categories')
                  .find(appointment.eventId);
                if (categoryEvent) {
                  category = categoryEvent?.name;
                }
              }

              curatedAppointments.push({
                id: appointment.id,
                date: date,
                location: appointment.location,
                type: appointment.type,
                start: start,
                end: end,
                title: appointment.eventId ? category : clientName,
                flagColor: appointmentColor,
                sessionStatus,
                staffColor: userStore?.[staffParticipant?.userId]?.color,
                textColor: userStore?.[staffParticipant?.userId]?.textColor,
                user: staffParticipant.userId,
                cancellationReason: appointment.cancellationReason,
                inProgress:
                  appointmentColor !== Colors.TEAL_500 &&
                  appointment.type !== 'event',
                isRecurring: true,
                deletedBy: appointment?.deletedBy,
                appointmentChanged,
                originalStart: apptStartDate,
                originalEnd: apptEndDate,
                newStart: sessionStartDate,
                newEnd: sessionEndDate,
              });
            }
          }
        }
      } else {
        for (const staffParticipant of staffParticipants) {
          if (
            !Object.keys(userStore).length ||
            userStore?.[staffParticipant.userId]
          ) {
            const {appointmentColor, sessionStatus} = await getAppointmentColor(
              appointment,
              appointment.date,
              staffParticipant.supervision,
            );

            let queries = [];
            if (staffParticipant?.supervision) {
              queries.push(Q.where('type', 'supervision'));
            } else {
              queries.push(Q.where('type', 'session'));
            }
            let apptSessions = await database
              .get(Session.table)
              .query(
                Q.where('appointment_id', appointment.id),
                Q.where(
                  'session_date',
                  moment(appointment.date).format('YYYY-MM-DD'),
                ),
                Q.where('deleted_at', null),
                Q.where('end_timestamp', Q.notEq(null)),
                ...queries,
              )
              .fetch();
            let useSessionTime = apptSessions?.length > 0;
            //check if end times are different thn what is scheduled
            let appointmentChanged = false;
            let sessionStartDate;
            let sessionEndDate;
            let apptStartDate = moment(staffParticipant.startTime);
            let apptEndDate = moment(staffParticipant.endTime);
            const startDate = useSessionTime
              ? moment(apptSessions[0]?.editedStartTimestamp)
              : apptStartDate;
            const endDate = useSessionTime
              ? moment(apptSessions[0]?.editedEndTimestamp)
              : apptEndDate;
            if (useSessionTime) {
              sessionStartDate = moment(apptSessions[0]?.editedStartTimestamp);
              sessionEndDate = moment(apptSessions[0]?.editedEndTimestamp);

              if (
                sessionStartDate.format('HH:mm') !==
                  apptStartDate.format('HH:mm') ||
                sessionEndDate.format('HH:mm') !== apptEndDate.format('HH:mm')
              ) {
                appointmentChanged = true;
              }
            }

            const start = moment(appointment.date)
              .set({
                hour: startDate.get('hour'),
                minute: startDate.get('minute'),
                second: startDate.get('second'),
              })
              .toDate();
            const end = moment(appointment.date)
              .set({
                hour: endDate.get('hour'),
                minute: endDate.get('minute'),
                second: endDate.get('second'),
              })
              .toDate();
            let category = 'General Event';
            if (appointment.type === 'event' && appointment?.eventId) {
              const categoryEvent = await database
                .get('event_categories')
                .find(appointment.eventId);
              if (categoryEvent) {
                category = categoryEvent?.name;
              }
            }
            curatedAppointments.push({
              id: appointment.id,
              date: appointment.date,
              location: appointment.location,
              start,
              end,
              type: appointment.type,
              title: appointment.type === 'event' ? category : clientName,
              flagColor: appointmentColor,
              sessionStatus,
              staffColor: userStore?.[staffParticipant?.userId]?.color,
              textColor: userStore?.[staffParticipant?.userId]?.textColor,
              user: staffParticipant.userId,
              cancellationReason: appointment.cancellationReason,
              inProgress:
                appointmentColor !== Colors.TEAL_500 &&
                appointment.type !== 'event',
              isRecurring: false,
              cancellationMoreInfo: appointment.cancellationMoreInfo,
              deletedBy: appointment?.deletedBy,
              appointmentChanged,
              originalStart: apptStartDate,
              originalEnd: apptEndDate,
              newStart: sessionStartDate,
              newEnd: sessionEndDate,
            });
          }
        }
      }
    }
    setItems(curatedAppointments);
  };

  // Sync sessions for the requested date range
  useEffect(() => {
    if (dates?.start && dates?.end) {
      syncDateRange(
        startOfWeek(dates?.start, {weekStartsOn: 6}),
        endOfWeek(dates?.end),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates?.start, dates?.end]);

  // Recalculate appointments after sync is complete
  useEffect(() => {
    if (!isLoading && dates?.start && dates?.end) {
      recalculateAppointments(
        startOfWeek(dates?.start, {weekStartsOn: 6}),
        endOfWeek(dates?.end),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, filters, editRecurringAppointment, update, isLoading]);

  const defaultExportSpec = {
    headers: [
      'Date',
      'Start Time',
      'End Time',
      'Altered Start Time',
      'Altered End Time',
      'Client/Event',
      'Staff',
      'Place of Service',
      'Status',
      'Additional Comments',
    ],
    mapRow: ({
      item = {},
      appointment = {},
      session = null,
      patients = [],
      staff = [],
      placeOfService = '-',
      status = '-',
    }) => [
      moment(item?.date).format('MM/DD/YYYY'),
      moment(appointment?.start).format('hh:mm A'),
      moment(appointment?.end).format('hh:mm A'),
      session ? moment(session.editedStartTimestamp).format('hh:mm A') : '-',
      session ? moment(session.editedEndTimestamp).format('hh:mm A') : '-',
      patients.length
        ? patients
            .map(p => (p.firstName ? `${p.firstName} ${p.lastName}` : p.name))
            .join(', ')
        : '',
      staff.map(s => `${s.firstName} ${s.lastName}`).join(', '),
      placeOfService,
      status,
      appointment?.comment,
    ],
  };

  const exportSpecs = {
    cancel: {
      headers: [
        'Date',
        'Start Time',
        'End Time',
        'Client/Event',
        'Staff',
        'Cancelled By',
        'Cancellation Reason',
        'Additional Comments',
      ],
      mapRow: ({
        item = {},
        appointment = {},
        patients = [],
        staff = [],
        cancelledBy = null,
      }) => [
        moment(item?.date).format('MM/DD/YYYY'),
        moment(appointment?.start).format('hh:mm A'),
        moment(appointment?.end).format('hh:mm A'),
        patients.length
          ? patients
              .map(p => (p.firstName ? `${p.firstName} ${p.lastName}` : p.name))
              .join(', ')
          : '',
        staff.map(s => `${s.firstName} ${s.lastName}`).join(', '),
        cancelledBy ? `${cancelledBy.firstName} ${cancelledBy.lastName}` : '-',
        cancelReason(item?.cancellationReason)?.name +
          (item?.cancellationMoreInfo
            ? ' - ' + item?.cancellationMoreInfo
            : ''),
        appointment?.comment,
      ],
    },
    status: defaultExportSpec,
    location: defaultExportSpec,
    default: defaultExportSpec,
  };

  const exportAppointments = async () => {
    const spec = exportSpecs[filters.scheduleBy] || exportSpecs.default;
    const flatAppointments = [spec.headers];

    const filteredItems = items
      .filter((item: any) =>
        moment(new Date(item?.date)).isBetween(
          dates?.start,
          dates?.end,
          undefined,
          '[]',
        ),
      )
      .sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

    const rows = await Promise.all(
      filteredItems.map(async (item: any) => {
        const appointment = await database
          .get(Appointment.table)
          .find(item?.id);
        let session = null;
        const appointmentSession = await database
          .get(Session.table)
          .query(
            Q.where('appointment_id', appointment.id),
            Q.where('session_date', moment(item?.date).format('YYYY-MM-DD')),
            Q.where('deleted_at', null),
            Q.where('end_timestamp', Q.notEq(null)),
          )
          .fetch();
        if (appointmentSession.length) {
          let sessionStartDate = moment(
            appointmentSession[0]?.editedStartTimestamp,
          );
          let sessionEndDate = moment(
            appointmentSession[0]?.editedEndTimestamp,
          );
          let apptStartDate = moment(appointment.start);
          let apptEndDate = moment(appointment.end);

          if (
            sessionStartDate.format('HH:mm') !==
              apptStartDate.format('HH:mm') ||
            sessionEndDate.format('HH:mm') !== apptEndDate.format('HH:mm')
          ) {
            session = appointmentSession[0];
          }
        }
        let [patients, staff, cancelledBy] = await Promise.all([
          appointment.patients,
          appointment.staff,
          item?.deletedBy
            ? database.get(User.table).find(item?.deletedBy)
            : Promise.resolve(null),
        ]);
        if (!patients.length) {
          patients = await database
            .get('event_categories')
            .query(Q.where('id', appointment?.eventId));
        }
        const locationLabel =
          appointmentLocations.find(
            (loc: any) =>
              loc.value === (item?.location ?? appointment?.location),
          )?.label ?? '-';
        return spec.mapRow({
          item,
          appointment,
          session,
          patients,
          staff,
          cancelledBy,
          placeOfService: locationLabel,
          status: item?.sessionStatus ?? '-',
        });
      }),
    );

    flatAppointments.push(...rows);
    return flatAppointments;
  };

  const renderEventContent = (eventContent: EventContentArg) => {
    const layout = eventContent.view.type;

    const {
      flagColor,
      staffColor,
      cancellationReason,
      inProgress,
      cancellationMoreInfo,
      deletedBy,
      type,
      appointmentChanged,
    } = eventContent.event.extendedProps;
    const {textColor, start, end, title, id} = eventContent.event;
    const apptDuration = moment(end).diff(start, 'minutes');

    return inProgress && layout !== 'listWeek' ? (
      <InProgressCalendarItem
        title={title}
        layout={layout}
        start={start}
        end={end}
        apptDuration={apptDuration}
        staffColor={staffColor}
        textColor={textColor}
        flagColor={flagColor}
        appointmentChanged={appointmentChanged}
      />
    ) : layout === 'listWeek' && filters.scheduleBy === 'cancel' ? (
      <CancellationListItem
        title={title}
        cancellationReason={cancellationReason}
        cancellationMoreInfo={cancellationMoreInfo}
        deletedBy={deletedBy}
      />
    ) : layout === 'listWeek' ? (
      <AppointmentListItem
        eventContent={eventContent}
        title={title}
        flagColor={flagColor}
        appointmentId={id}
      />
    ) : (
      <AppointmentCalendarItem
        title={title}
        layout={layout}
        type={type}
        start={start}
        end={end}
        apptDuration={apptDuration}
        staffColor={staffColor}
        textColor={textColor}
        flagColor={flagColor}
      />
    );
  };

  const dragAppointment = async (event: any) => {
    const {inProgress, isRecurring} = event.event.extendedProps;
    if (inProgress) {
      setCannotMoveEvent(true);
      event.revert();
    } else {
      if (canEdit) {
        const {start, end} = event.event;

        if (isRecurring) {
          setEditRecurringAppointment(true);
          setEditRecurringAppointment(event);
        } else {
          const appointment = await database
            .get(Appointment.table)
            .find(event.event.id);

          for (const participant of await appointment.staffParticipants.fetch()) {
            const startDifference = Math.abs(
              appointment.start.getTime() - participant.startTime.getTime(),
            );
            const endDifference = Math.abs(
              appointment.end.getTime() - participant.endTime.getTime(),
            );

            await participant.updateEntity({
              startTime: new Date(start.getTime() - startDifference),
              endTime: new Date(end.getTime() - endDifference),
            });
          }
          await appointment.updateEntity({
            date: start,
            start,
            end,
          });
        }
      } else {
        event.revert();
      }
    }
  };

  const onSubmit = async (values: any) => {
    let eventId = editRecurringAppointment?.event?.event?.id
      ? editRecurringAppointment?.event?.event?.id
      : editRecurringAppointment?.event?.id;
    const appointment = await database.get('appointments').find(eventId);
    const rruleSet = rrulestr(appointment.rrule, {
      forceset: true,
    });

    const {start, end} =
      editRecurringAppointment?.event?.event || editRecurringAppointment?.event;
    const todaysDate = start;
    switch (values.action) {
      case 'event':
        rruleSet.exdate(
          new Date(
            todaysDate.getFullYear(),
            todaysDate.getMonth(),
            todaysDate.getDate(),
            0,
            0,
            0,
            0,
          ),
        );
        await appointment.updateEntity({
          rrule: rruleSet.toString(),
        });
        await database?.write(async () => {
          const createAppointment = await database
            ?.get(Appointment.table)
            .create(entity => {
              entity.partition = selectedGroup;
              entity.location = appointment.location;
              entity.title = appointment.title;
              entity.type = appointment.type;
              entity.date = todaysDate;
              entity.start = start;
              entity.end = end;
              entity.noteTemplateId = appointment.noteTemplateId;
              entity.createdBy = userId;
              entity.updatedBy = userId;
            });
          for (const participant of await appointment.staffParticipants.fetch()) {
            const startDifference = Math.abs(
              appointment.start.getTime() - participant.startTime.getTime(),
            );
            const endDifference = Math.abs(
              appointment.end.getTime() - participant.endTime.getTime(),
            );
            await database?.get(Participant.table).create(entity => {
              entity.partition = selectedGroup;
              entity.appointment.id = createAppointment.id;
              entity.userId = participant.userId;
              entity.patientId = participant.patientId;
              entity.billable = participant.billable;
              entity.supervision = participant.supervision;
              entity.startTime = new Date(start.getTime() - startDifference);
              entity.endTime = new Date(end.getTime() - endDifference);
              entity.deletedBy = participant.deletedBy;
              entity.deletedAt = participant.deletedAt;
              entity.createdBy = userId;
              entity.updatedBy = userId;
            });
          }
          for (const participant of await appointment.patientParticipants.fetch()) {
            await database?.get(Participant.table).create(entity => {
              entity.partition = selectedGroup;
              entity.appointment.id = createAppointment.id;
              entity.userId = participant.userId;
              entity.patientId = participant.patientId;
              entity.billable = participant.billable;
              entity.supervision = participant.supervision;
              entity.startTime = start;
              entity.endTime = end;
              entity.deletedBy = participant.deletedBy;
              entity.deletedAt = participant.deletedAt;
              entity.createdBy = userId;
              entity.updatedBy = userId;
            });
          }
        });
        break;
      case 'following':
        const ruleSet = new RRuleSet();
        for (const exrule of rruleSet.exrules()) {
          ruleSet.exrule(exrule);
        }
        for (const rdate of rruleSet.rdates()) {
          ruleSet.rdate(rdate);
        }
        for (const exdate of rruleSet.exdates()) {
          ruleSet.exdate(exdate);
        }
        ruleSet.rrule(
          new RRule({
            ...rruleSet.rrules()[0].options,
            until: new Date(
              todaysDate.getFullYear(),
              todaysDate.getMonth(),
              todaysDate.getDate() - 1,
              22,
              59,
            ).setMinutes(moment(rruleSet._dtstart).utcOffset()),
          }),
        );

        await appointment.updateEntity({
          rrule: ruleSet.toString(),
        });

        const rrule = new RRuleSet();
        for (const exrule of rruleSet.exrules()) {
          rrule.exrule(exrule);
        }
        for (const rdate of rruleSet.rdates()) {
          rrule.rdate(rdate);
        }
        for (const exdate of rruleSet.exdates()) {
          rrule.exdate(exdate);
        }

        rrule.rrule(
          new RRule({
            ...rruleSet.rrules()[0].options,
            dtstart: new Date(
              todaysDate.getFullYear(),
              todaysDate.getMonth(),
              todaysDate.getDate(),
            ),
          }),
        );

        await database?.write(async () => {
          const createAppointment = await database
            ?.get(Appointment.table)
            .create(entity => {
              entity.partition = selectedGroup;
              entity.location = appointment.location;
              entity.type = appointment.type;
              entity.entityId = appointment.entityId;
              entity.date = todaysDate;
              entity.start = start;
              entity.end = end;
              entity.noteTemplateId = appointment.noteTemplateId;
              entity.rrule = rrule.toString();
              entity.createdBy = userId;
              entity.updatedBy = userId;
            });
          for (const participant of await appointment.staffParticipants.fetch()) {
            const startDifference = Math.abs(
              appointment.start.getTime() - participant.startTime.getTime(),
            );
            const endDifference = Math.abs(
              appointment.end.getTime() - participant.endTime.getTime(),
            );
            await database?.get(Participant.table).create(entity => {
              entity.partition = selectedGroup;
              entity.appointment.id = createAppointment.id;
              entity.userId = participant.userId;
              entity.patientId = participant.patientId;
              entity.billable = participant.billable;
              entity.supervision = participant.supervision;
              entity.startTime = new Date(start.getTime() - startDifference);
              entity.endTime = new Date(end.getTime() - endDifference);
              entity.deletedBy = participant.deletedBy;
              entity.deletedAt = participant.deletedAt;
              entity.createdBy = userId;
              entity.updatedBy = userId;
            });
          }
          for (const participant of await appointment.patientParticipants.fetch()) {
            await database?.get(Participant.table).create(entity => {
              entity.partition = selectedGroup;
              entity.appointment.id = createAppointment.id;
              entity.userId = participant.userId;
              entity.patientId = participant.patientId;
              entity.billable = participant.billable;
              entity.supervision = participant.supervision;
              entity.startTime = start;
              entity.endTime = end;
              entity.deletedBy = participant.deletedBy;
              entity.deletedAt = participant.deletedAt;
              entity.createdBy = userId;
              entity.updatedBy = userId;
            });
          }
          return createAppointment;
        });
        break;
      case 'all':
        for (const participant of await appointment.staffParticipants.fetch()) {
          const startDifference = Math.abs(
            appointment.start.getTime() - participant.startTime.getTime(),
          );
          const endDifference = Math.abs(
            appointment.end.getTime() - participant.endTime.getTime(),
          );

          await participant.updateEntity({
            startTime: new Date(start.getTime() - startDifference),
            endTime: new Date(end.getTime() - endDifference),
          });
        }

        await appointment.updateEntity({
          date: start,
          start,
          end,
        });
        break;
    }
    setEditRecurringAppointment(false);
  };
  return (
    <>
      <Spinner visible={isLoading} />

      <EditRecurringAppointment
        show={[editRecurringAppointment, setEditRecurringAppointment]}
        cancelled={() => setEditRecurringAppointment(false)}
        onSubmit={onSubmit}
      />
      <NotDraggableModal show={[cannotMoveEvent, setCannotMoveEvent]} />
      {selectedEvent ? (
        <CalendarCard
          type={selectedEvent.event.extendedProps.type}
          date={selectedEvent.event.start}
          anchor={{
            x: selectedEvent.jsEvent.x,
            y: selectedEvent.jsEvent.y,
          }}
          event={selectedEvent.event}
          canEdit={canEdit}
          editCallback={(appointment: any) => {
            setSelectedEvent(false);
            editCallback(appointment, selectedEvent.event.start);
          }}
          canDelete={canDelete}
          deleteCallback={(
            appointment: any,
            supervision: boolean,
            dtSession: any,
          ) => {
            setSelectedEvent(false);
            deleteCallback(
              appointment,
              selectedEvent.event.start,
              supervision,
              dtSession,
            );
          }}
          onClose={() => setSelectedEvent(false)}
        />
      ) : null}
      <StyleWrapper>
        <FullCalendar
          slotEventOverlap={false}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            listPlugin,
          ]}
          editable={!!canEdit}
          height={dimensions.window.height - 116}
          selectable={true}
          events={items}
          eventContent={renderEventContent}
          initialView={
            filters?.scheduleBy === 'cancel'
              ? 'listWeek'
              : filters?.calendarView || 'timeGridWeek'
          }
          initialDate={
            filters?.calendarDate ? new Date(filters.calendarDate) : new Date()
          }
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
          }}
          datesSet={dateInfo => {
            setDates(dateInfo);
            // For month view, use the midpoint between start and end to get a date
            // that's actually in the month being viewed (not from previous month)
            let representativeDate = dateInfo.start;
            if (dateInfo.view.type === 'dayGridMonth') {
              const startTime = dateInfo.start.getTime();
              const endTime = dateInfo.end.getTime();
              representativeDate = new Date((startTime + endTime) / 2);
            }
            dispatch(
              setFilters({
                ...filters,
                calendarView: dateInfo.view.type,
                calendarDate: representativeDate.getTime(),
              }),
            );
          }}
          dayMaxEventRows={true}
          views={{
            dayGrid: {
              eventLimit: 4,
              dayMaxEventRows: 5,
            },
          }}
          buttonText={{
            today: 'TODAY',
            month: 'MONTH',
            week: 'WEEK',
            day: 'DAY',
            list: 'LIST',
          }}
          nowIndicator={false}
          eventClick={setSelectedEvent}
          eventDrop={dragAppointment}
          eventResize={dragAppointment}
          dateClick={canAdd ? addCallback : () => {}}
        />
        <View style={[styles.row, styles.justifySpaceBetween]}>
          <View />
          {filters.scheduleBy === 'status' ? (
            <StatusLegend />
          ) : filters.scheduleBy === 'location' ? (
            <LocationLegend />
          ) : filters.scheduleBy === 'cancel' ? (
            <CancellationLegend />
          ) : null}
          <View style={[styles.paddingRight, styles.paddingSMTop]}>
            <CsvExport
              data={exportAppointments}
              name={`${moment(dates?.start).format('MM/DD/YY')} - ${moment(
                dates?.end,
              ).format('MM/DD/YY')}`}
            />
          </View>
        </View>
      </StyleWrapper>
    </>
  );
};

export default Calendar;
